import { showImperativeToast } from '../providers/UIProvider';
import { shouldSuppressAuthReauthPlayToast } from '../lib/authHealth';
import { useMaestroStore } from '../store/maestroStore';
import {
  classifyLocalPlayError,
  dispatchFdaPermissionRequired,
  type LocalPlayErrorKind,
} from './localPlayErrors';
import { applyUnmountedDriveHeuristic } from './unmountedDriveHeuristic';
import { rewriteNetworkPlayError } from './playFailureUI';
import { rewritePlayErrorIfProxyFetchBlocked } from './proxyFetchFailedUI';

const TOAST_MS = 8000;

function toastForKind(kind: LocalPlayErrorKind, trackTitle: string): string {
  switch (kind) {
    case 'fda_permission_required':
      return `"${trackTitle}" — Maestro needs Full Disk Access to play files from this drive.`;
    case 'outside_library_roots':
      return `"${trackTitle}" — this file is outside your configured library folders. Re-add the folder in Add Music.`;
    case 'file_not_found':
      return `"${trackTitle}" — file not found. It may have been moved or deleted.`;
    default:
      return `"${trackTitle}" — couldn't play this file.`;
  }
}

/** User-facing feedback for local library play failures. */
export function handleLocalPlayFailure(
  errMsg: string,
  trackTitle = 'This track',
  filePath?: string | null,
): void {
  if (shouldSuppressAuthReauthPlayToast(errMsg)) {
    return;
  }
  const { kind } = classifyLocalPlayError(errMsg);
  if (kind === 'fda_permission_required') {
    dispatchFdaPermissionRequired();
  }
  if (applyUnmountedDriveHeuristic(errMsg, filePath)) {
    return;
  }
  if (kind === 'generic') {
    const store = useMaestroStore.getState();
    const rendererId = store.activeConnectorRenderer?.id || store.discovery?.activeZone?.id;
    const rendererName = store.activeConnectorRenderer?.name || store.discovery?.activeZone?.name;
    const formatRewritten = rewriteNetworkPlayError(
      errMsg,
      { title: trackTitle },
      store.currentStreamFormatId,
      rendererName,
    );
    const rewritten = rewritePlayErrorIfProxyFetchBlocked(formatRewritten, rendererId, rendererName);
    if (rewritten !== errMsg) {
      showImperativeToast(rewritten, TOAST_MS);
      return;
    }
  }
  showImperativeToast(toastForKind(kind, trackTitle), TOAST_MS);
}
